import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FileText, Save, RotateCcw, ChevronUp, ChevronDown, Plus, Trash2,
    Sparkles, BarChart3, History, AlertCircle, User, ImageIcon, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminSiteContent,
    saveAdminSiteContent,
    resetAdminSiteContent,
} from "@/lib/adminApi";

const TABS = [
    { id: "branding", label: "Branding", icon: ImageIcon },
    { id: "hero", label: "Hero", icon: Sparkles },
    { id: "stats", label: "Stats", icon: BarChart3 },
    { id: "about", label: "About", icon: User },
    { id: "work_history", label: "Work History", icon: History },
];

const newId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

export default function AdminContent() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("branding");
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminSiteContent();
            setContent(data);
            setDirty(false);
        } catch (_err) {
            toast.error("Could not load site content");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const update = useCallback((path, value) => {
        setContent((c) => {
            if (!c) return c;
            const next = structuredClone(c);
            const keys = path.split(".");
            let cur = next;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys[keys.length - 1]] = value;
            return next;
        });
        setDirty(true);
    }, []);

    const save = async () => {
        if (!content) return;
        setSaving(true);
        try {
            const data = await saveAdminSiteContent(content);
            setContent(data);
            setDirty(false);
            toast.success("Saved", { description: "Changes are live on the site." });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
            toast.error("Could not save", { description: msg || "Please try again." });
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = async () => {
        if (!window.confirm("Reset all content to the original code defaults? This cannot be undone.")) return;
        setSaving(true);
        try {
            const data = await resetAdminSiteContent();
            setContent(data);
            setDirty(false);
            toast.success("Reset to defaults");
        } catch (_err) {
            toast.error("Reset failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5" /> Studio CMS · Content Editor
                    </span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-content-headline"
                    >
                        Site Content
                    </h1>
                    <p className="mt-2 max-w-xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Hero, stats, and work history. Use{" "}
                        <code className="font-mono text-[12px]" style={{ color: "var(--pg-fg)" }}>{"{gold:text}"}</code> for animated
                        gold accents and{" "}
                        <code className="font-mono text-[12px]" style={{ color: "var(--pg-fg)" }}>{"{italic:text}"}</code> for italic
                        underlined accents.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        disabled={saving || loading}
                        data-testid="admin-content-reset"
                        className="pg-btn-ghost pg-app-tap disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset Defaults
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={!dirty || saving || loading}
                        data-testid="admin-content-save"
                        className="pg-btn-primary pg-app-tap disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
                    </button>
                </div>
            </div>

            {/* Dirty notice */}
            <AnimatePresence>
                {dirty && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="mt-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-xs"
                        style={{
                            borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                            background: "color-mix(in srgb, var(--pg-accent) 8%, transparent)",
                            color: "var(--pg-fg)",
                        }}
                        data-testid="admin-content-dirty-notice"
                    >
                        <AlertCircle className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} />
                        Unsaved changes — remember to hit Save.
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tabs */}
            <div className="mt-6 inline-flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--pg-border)" }}>
                {TABS.map((t) => {
                    const active = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActiveTab(t.id)}
                            data-testid={`admin-content-tab-${t.id}`}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                            style={{
                                background: active ? "var(--pg-fg)" : "transparent",
                                color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                            }}
                        >
                            <t.icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Body */}
            <div className="mt-6">
                {loading || !content ? (
                    <div
                        className="flex items-center justify-center rounded-2xl border px-6 py-20 text-sm"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    >
                        Loading…
                    </div>
                ) : (
                    <>
                        {activeTab === "branding" && (
                            <BrandingEditor
                                logos={content.logos || { dark_theme_url: "", light_theme_url: "", alt_text: "Pranto.Gfx" }}
                                heroRailSpeed={content.hero_rail_speed || "normal"}
                                update={update}
                            />
                        )}
                        {activeTab === "hero" && <HeroEditor hero={content.hero} update={update} />}
                        {activeTab === "stats" && <StatsEditor stats={content.stats} update={update} />}
                        {activeTab === "about" && <AboutEditor about={content.about || {}} update={update} />}
                        {activeTab === "work_history" && <WorkHistoryEditor wh={content.work_history} update={update} />}
                    </>
                )}
            </div>
        </div>
    );
}

// ---------------- Branding editor (logos + hero rail speed) ----------------

const SPEED_STEPS = [
    { value: "super_slow", label: "Super Slow" },
    { value: "slow", label: "Slow" },
    { value: "normal", label: "Normal" },
    { value: "fast", label: "Fast" },
    { value: "super_fast", label: "Super Fast" },
];

const BrandingEditor = ({ logos, heroRailSpeed, update }) => {
    const setLogo = (which, url) => update(`logos.${which}`, url);
    const currentSpeedIdx = Math.max(0, SPEED_STEPS.findIndex((s) => s.value === heroRailSpeed));

    return (
        <>
            <SectionCard
                title="Header Logos"
                subtitle="Upload separate marks for dark and light themes. Recommended: transparent PNG or SVG, 200–400px wide."
            >
                <div className="grid gap-6 lg:grid-cols-2">
                    <LogoField
                        label="Dark Theme Logo"
                        helper="Shown on the dark website theme. Upload a white / light-coloured version of your logo."
                        value={logos.dark_theme_url || ""}
                        onChange={(url) => setLogo("dark_theme_url", url)}
                        previewBg="linear-gradient(135deg, #062E2D 0%, #050B0C 100%)"
                        testId="admin-content-logo-dark"
                    />
                    <LogoField
                        label="Light Theme Logo"
                        helper="Shown on the light website theme. Upload a black / dark-coloured version of your logo."
                        value={logos.light_theme_url || ""}
                        onChange={(url) => setLogo("light_theme_url", url)}
                        previewBg="linear-gradient(135deg, #F7F3E7 0%, #FFFFFF 100%)"
                        previewTextColor="#111"
                        testId="admin-content-logo-light"
                    />
                </div>
                <div className="mt-6">
                    <Field
                        label="Alt / accessibility text"
                        value={logos.alt_text || ""}
                        onChange={(v) => update("logos.alt_text", v)}
                        placeholder="Pranto.Gfx"
                        testId="admin-content-logo-alt"
                    />
                </div>
            </SectionCard>

            <SectionCard
                title="Hero Rail Speed"
                subtitle="Controls how quickly the curved image rail behind the hero moves left to right."
            >
                <div
                    className="rounded-2xl border p-5"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="inline-flex items-center gap-2">
                            <Gauge className="h-4 w-4" style={{ color: "var(--pg-accent)" }} />
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                Current
                            </span>
                            <span className="font-display text-sm" style={{ color: "var(--pg-fg)" }}>
                                {SPEED_STEPS[currentSpeedIdx]?.label || "Normal"}
                            </span>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--pg-fg-muted)" }}>
                            Live on the hero
                        </span>
                    </div>

                    <input
                        type="range"
                        min={0}
                        max={SPEED_STEPS.length - 1}
                        step={1}
                        value={currentSpeedIdx}
                        onChange={(e) => update("hero_rail_speed", SPEED_STEPS[Number(e.target.value)].value)}
                        className="pg-speed-slider mt-5 w-full"
                        data-testid="admin-content-hero-rail-speed"
                        aria-label="Hero rail speed"
                    />

                    <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--pg-fg-muted)" }}>
                        {SPEED_STEPS.map((s) => (
                            <button
                                key={s.value}
                                type="button"
                                onClick={() => update("hero_rail_speed", s.value)}
                                className="pg-app-tap px-1"
                                style={{ color: s.value === heroRailSpeed ? "var(--pg-accent)" : undefined }}
                                data-testid={`admin-content-hero-rail-speed-${s.value}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                <p className="mt-4 text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                    Manage the actual images in{" "}
                    <a href="/admin/hero-rail" className="pg-link-underline" style={{ color: "var(--pg-accent)" }}>
                        Hero Rail →
                    </a>
                </p>
            </SectionCard>
        </>
    );
};

const LogoField = ({ label, helper, value, onChange, previewBg, previewTextColor = "#F7F3E7", testId }) => (
    <div className="rounded-2xl border p-4" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
        <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>{label}</span>
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]"
                    style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    data-testid={`${testId}-clear`}
                >
                    <Trash2 className="h-3 w-3" /> Clear
                </button>
            )}
        </div>

        <div
            className="mt-3 flex h-24 w-full items-center justify-center overflow-hidden rounded-xl border"
            style={{ borderColor: "var(--pg-border)", background: previewBg }}
        >
            {value ? (
                <img
                    src={value}
                    alt={`${label} preview`}
                    className="max-h-full max-w-[80%] object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            ) : (
                <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: previewTextColor, opacity: 0.7 }}>
                    Preview — Upload URL Below
                </span>
            )}
        </div>

        <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="pg-input mt-3 w-full"
            placeholder="https://…/logo.svg"
            data-testid={testId}
        />
        <p className="mt-2 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>{helper}</p>
    </div>
);


// ---------------- Hero editor ----------------

const HeroEditor = ({ hero, update }) => {
    const setHeadline = (idx, value) => {
        const lines = [...(hero.headline_lines || [])];
        lines[idx] = value;
        update("hero.headline_lines", lines);
    };
    const addHeadline = () => update("hero.headline_lines", [...(hero.headline_lines || []), ""]);
    const removeHeadline = (idx) => {
        const lines = (hero.headline_lines || []).filter((_, i) => i !== idx);
        update("hero.headline_lines", lines);
    };
    const moveHeadline = (idx, dir) => {
        const lines = [...(hero.headline_lines || [])];
        const target = idx + dir;
        if (target < 0 || target >= lines.length) return;
        [lines[idx], lines[target]] = [lines[target], lines[idx]];
        update("hero.headline_lines", lines);
    };
    const setRotating = (idx, value) => {
        const w = [...(hero.rotating_words || [])];
        w[idx] = value;
        update("hero.rotating_words", w);
    };
    const addRotating = () => update("hero.rotating_words", [...(hero.rotating_words || []), ""]);
    const removeRotating = (idx) => update("hero.rotating_words", (hero.rotating_words || []).filter((_, i) => i !== idx));

    return (
        <SectionCard title="Hero" subtitle="The first thing visitors read. Use rich tokens for gold/italic accents.">
            <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Eyebrow Text" value={hero.eyebrow || ""} onChange={(v) => update("hero.eyebrow", v)} testId="admin-content-hero-eyebrow" />
                <Field label="Chip Text" value={hero.chip || ""} onChange={(v) => update("hero.chip", v)} testId="admin-content-hero-chip" placeholder="Since 2013" />
            </div>

            <div className="mt-6">
                <SubLabel>Headline Lines</SubLabel>
                <p className="mb-3 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                    Each line renders on its own row. Use{" "}
                    <code className="font-mono text-[11px]" style={{ color: "var(--pg-fg)" }}>{"{gold:Premium}"}</code> or
                    <code className="ml-1 font-mono text-[11px]" style={{ color: "var(--pg-fg)" }}>{"{italic:Strategic}"}</code> inline.
                </p>
                <ul className="space-y-2" data-testid="admin-content-hero-headlines">
                    {(hero.headline_lines || []).map((line, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={line}
                                onChange={(e) => setHeadline(idx, e.target.value)}
                                className="pg-input flex-1"
                                placeholder={`Line ${idx + 1}`}
                                data-testid={`admin-content-hero-headline-${idx}`}
                            />
                            <ReorderButton onUp={() => moveHeadline(idx, -1)} onDown={() => moveHeadline(idx, 1)} />
                            <IconBtn onClick={() => removeHeadline(idx)} aria-label="Remove line" testId={`admin-content-hero-headline-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    onClick={addHeadline}
                    className="pg-btn-ghost pg-app-tap mt-3"
                    data-testid="admin-content-hero-headline-add"
                >
                    <Plus className="h-3.5 w-3.5" /> Add Line
                </button>
            </div>

            <div className="mt-6">
                <SubLabel>Description</SubLabel>
                <textarea
                    rows={4}
                    value={hero.description || ""}
                    onChange={(e) => update("hero.description", e.target.value)}
                    className="pg-input mt-1.5"
                    style={{ resize: "vertical", minHeight: 110 }}
                    data-testid="admin-content-hero-description"
                />
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="Primary Button Label" value={hero.primary_cta?.label || ""} onChange={(v) => update("hero.primary_cta.label", v)} testId="admin-content-hero-primary-cta" placeholder="View Portfolio" />
                <Field label="Ghost Button Label" value={hero.ghost_cta?.label || ""} onChange={(v) => update("hero.ghost_cta.label", v)} testId="admin-content-hero-ghost-cta" placeholder="Start A Project" />
            </div>

            <div className="mt-6">
                <SubLabel>Rotating Words (&quot;Now Crafting&quot; pill)</SubLabel>
                <ul className="mt-2 space-y-2" data-testid="admin-content-hero-rotating">
                    {(hero.rotating_words || []).map((w, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={w}
                                onChange={(e) => setRotating(idx, e.target.value)}
                                className="pg-input flex-1"
                                data-testid={`admin-content-hero-rotating-${idx}`}
                            />
                            <IconBtn onClick={() => removeRotating(idx)} aria-label="Remove" testId={`admin-content-hero-rotating-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={addRotating} className="pg-btn-ghost pg-app-tap mt-3" data-testid="admin-content-hero-rotating-add">
                    <Plus className="h-3.5 w-3.5" /> Add Word
                </button>
            </div>
        </SectionCard>
    );
};

// ---------------- Stats editor ----------------

const StatsEditor = ({ stats, update }) => {
    const items = stats?.items || [];
    const setItem = (idx, key, value) => {
        const next = items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
        update("stats.items", next);
    };
    const addItem = () => update("stats.items", [...items, { label: "New Stat", value: "0", highlight: true }]);
    const removeItem = (idx) => update("stats.items", items.filter((_, i) => i !== idx));
    const moveItem = (idx, dir) => {
        const arr = [...items];
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        update("stats.items", arr);
    };

    return (
        <SectionCard title="Hero Stats" subtitle="The credibility row beneath the hero CTAs.">
            <ul className="space-y-3" data-testid="admin-content-stats-list">
                {items.map((it, idx) => (
                    <li
                        key={idx}
                        className="grid grid-cols-1 gap-3 rounded-2xl border p-4 sm:grid-cols-[1fr_1fr_auto_auto]"
                        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                        data-testid={`admin-content-stat-${idx}`}
                    >
                        <Field label="Value" value={it.value || ""} onChange={(v) => setItem(idx, "value", v)} placeholder="350+" testId={`admin-content-stat-value-${idx}`} />
                        <Field label="Label" value={it.label || ""} onChange={(v) => setItem(idx, "label", v)} placeholder="High-Value Brands" testId={`admin-content-stat-label-${idx}`} />
                        <div className="flex items-end gap-2">
                            <label className="inline-flex items-center gap-2 text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                                <input
                                    type="checkbox"
                                    checked={!!it.highlight}
                                    onChange={(e) => setItem(idx, "highlight", e.target.checked)}
                                    data-testid={`admin-content-stat-highlight-${idx}`}
                                />
                                Gold
                            </label>
                        </div>
                        <div className="flex items-end justify-end gap-2">
                            <ReorderButton onUp={() => moveItem(idx, -1)} onDown={() => moveItem(idx, 1)} />
                            <IconBtn onClick={() => removeItem(idx)} aria-label="Remove stat" testId={`admin-content-stat-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </div>
                    </li>
                ))}
            </ul>
            <button type="button" onClick={addItem} className="pg-btn-ghost pg-app-tap mt-4" data-testid="admin-content-stats-add">
                <Plus className="h-3.5 w-3.5" /> Add Stat
            </button>
        </SectionCard>
    );
};

// ---------------- About editor ----------------

const AboutEditor = ({ about, update }) => {
    const chips = about.founder_role_chips || [];
    const paragraphs = about.paragraphs || [];
    const specialties = about.specialties || [];

    const setListAt = (key, idx, value) => {
        const next = [...(about[key] || [])];
        next[idx] = value;
        update(`about.${key}`, next);
    };
    const addToList = (key, fallback = "") => update(`about.${key}`, [...(about[key] || []), fallback]);
    const removeFromList = (key, idx) => update(`about.${key}`, (about[key] || []).filter((_, i) => i !== idx));
    const moveInList = (key, idx, dir) => {
        const arr = [...(about[key] || [])];
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        update(`about.${key}`, arr);
    };

    return (
        <SectionCard title="About Section" subtitle="The 'Pranto' card, headline narrative, paragraphs, and specialty chips.">
            <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Eyebrow Text" value={about.eyebrow || ""} onChange={(v) => update("about.eyebrow", v)} testId="admin-content-about-eyebrow" placeholder="About · The Studio" />
                <Field label="Founder Display Name" value={about.founder_name || ""} onChange={(v) => update("about.founder_name", v)} testId="admin-content-about-founder-name" placeholder="Pranto" />
            </div>

            <div className="mt-6">
                <SubLabel>Founder Image URL (the photo on the About card)</SubLabel>
                <input
                    type="url"
                    value={about.image || ""}
                    onChange={(e) => update("about.image", e.target.value)}
                    placeholder="https://…"
                    className="pg-input mt-1.5"
                    data-testid="admin-content-about-image"
                />
                {about.image && (
                    <div className="mt-3 overflow-hidden rounded-2xl border" style={{ borderColor: "var(--pg-border)", maxWidth: 280, aspectRatio: "4/5" }}>
                        <img
                            src={about.image}
                            alt="About preview"
                            className="h-full w-full object-cover"
                            style={{ objectPosition: about.image_position || "50% 30%" }}
                            onError={(e) => { e.target.style.opacity = 0.2; }}
                        />
                    </div>
                )}
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field
                    label="Image Crop / Position (CSS object-position)"
                    value={about.image_position || ""}
                    onChange={(v) => update("about.image_position", v)}
                    testId="admin-content-about-image-position"
                    placeholder="50% 30%"
                />
                <Field
                    label="Founder Chip Label"
                    value={about.founder_chip_label || ""}
                    onChange={(v) => update("about.founder_chip_label", v)}
                    testId="admin-content-about-chip-label"
                    placeholder="Founder · Studio Lead"
                />
            </div>

            <div className="mt-6">
                <SubLabel>Founder Role Chips (shown on the image card)</SubLabel>
                <ul className="mt-2 space-y-2" data-testid="admin-content-about-role-chips">
                    {chips.map((c, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={c}
                                onChange={(e) => setListAt("founder_role_chips", idx, e.target.value)}
                                className="pg-input flex-1"
                                data-testid={`admin-content-about-chip-${idx}`}
                            />
                            <ReorderButton onUp={() => moveInList("founder_role_chips", idx, -1)} onDown={() => moveInList("founder_role_chips", idx, 1)} />
                            <IconBtn onClick={() => removeFromList("founder_role_chips", idx)} aria-label="Remove chip" testId={`admin-content-about-chip-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={() => addToList("founder_role_chips", "")} className="pg-btn-ghost pg-app-tap mt-3" data-testid="admin-content-about-chip-add">
                    <Plus className="h-3.5 w-3.5" /> Add Chip
                </button>
            </div>

            <div className="mt-6">
                <SubLabel>Headline (use {"{gold:text}"} for animated gold accent)</SubLabel>
                <input
                    type="text"
                    value={about.headline || ""}
                    onChange={(e) => update("about.headline", e.target.value)}
                    className="pg-input mt-1.5"
                    data-testid="admin-content-about-headline"
                />
            </div>

            <div className="mt-6">
                <SubLabel>Narrative Paragraphs</SubLabel>
                <ul className="mt-2 space-y-2" data-testid="admin-content-about-paragraphs">
                    {paragraphs.map((p, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                            <textarea
                                rows={3}
                                value={p}
                                onChange={(e) => setListAt("paragraphs", idx, e.target.value)}
                                className="pg-input flex-1"
                                style={{ resize: "vertical", minHeight: 80 }}
                                data-testid={`admin-content-about-paragraph-${idx}`}
                            />
                            <div className="flex flex-col gap-1.5">
                                <ReorderButton onUp={() => moveInList("paragraphs", idx, -1)} onDown={() => moveInList("paragraphs", idx, 1)} />
                                <IconBtn onClick={() => removeFromList("paragraphs", idx)} aria-label="Remove paragraph" testId={`admin-content-about-paragraph-remove-${idx}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </IconBtn>
                            </div>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={() => addToList("paragraphs", "")} className="pg-btn-ghost pg-app-tap mt-3" data-testid="admin-content-about-paragraph-add">
                    <Plus className="h-3.5 w-3.5" /> Add Paragraph
                </button>
            </div>

            <div className="mt-6">
                <SubLabel>Specialty Chips (under the narrative)</SubLabel>
                <ul className="mt-2 space-y-2" data-testid="admin-content-about-specialties">
                    {specialties.map((s, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={s}
                                onChange={(e) => setListAt("specialties", idx, e.target.value)}
                                className="pg-input flex-1"
                                data-testid={`admin-content-about-specialty-${idx}`}
                            />
                            <ReorderButton onUp={() => moveInList("specialties", idx, -1)} onDown={() => moveInList("specialties", idx, 1)} />
                            <IconBtn onClick={() => removeFromList("specialties", idx)} aria-label="Remove specialty" testId={`admin-content-about-specialty-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={() => addToList("specialties", "")} className="pg-btn-ghost pg-app-tap mt-3" data-testid="admin-content-about-specialty-add">
                    <Plus className="h-3.5 w-3.5" /> Add Specialty
                </button>
            </div>
        </SectionCard>
    );
};

// ---------------- Work History editor ----------------

const WorkHistoryEditor = ({ wh, update }) => {
    const titleLines = wh.title_lines || [];
    const collab = wh.collab_brands || [];
    const featured = wh.featured || [];
    const current = wh.current_roles || [];

    const setTitleLine = (idx, v) => {
        const arr = [...titleLines];
        arr[idx] = v;
        update("work_history.title_lines", arr);
    };
    const addTitleLine = () => update("work_history.title_lines", [...titleLines, ""]);
    const removeTitleLine = (idx) => update("work_history.title_lines", titleLines.filter((_, i) => i !== idx));

    const collabSorted = useMemo(() => [...collab].sort((a, b) => (a.order || 0) - (b.order || 0)), [collab]);
    const featuredSorted = useMemo(() => [...featured].sort((a, b) => (a.order || 0) - (b.order || 0)), [featured]);
    const currentSorted = useMemo(() => [...current].sort((a, b) => (a.order || 0) - (b.order || 0)), [current]);

    const reorder = (arr, idx, dir) => {
        const next = [...arr];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return next;
        [next[idx], next[target]] = [next[target], next[idx]];
        return next.map((it, i) => ({ ...it, order: i }));
    };

    return (
        <SectionCard title="Work History" subtitle="Section header, brand tiles, featured rows, and current roles.">
            <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Eyebrow Text" value={wh.eyebrow || ""} onChange={(v) => update("work_history.eyebrow", v)} testId="admin-content-wh-eyebrow" />
            </div>

            <div className="mt-6">
                <SubLabel>Title Lines (use {"{gold:text}"} for animated gold)</SubLabel>
                <ul className="mt-2 space-y-2" data-testid="admin-content-wh-titles">
                    {titleLines.map((line, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={line}
                                onChange={(e) => setTitleLine(idx, e.target.value)}
                                className="pg-input flex-1"
                                data-testid={`admin-content-wh-title-${idx}`}
                            />
                            <IconBtn onClick={() => removeTitleLine(idx)} aria-label="Remove" testId={`admin-content-wh-title-remove-${idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={addTitleLine} className="pg-btn-ghost pg-app-tap mt-3" data-testid="admin-content-wh-title-add">
                    <Plus className="h-3.5 w-3.5" /> Add Line
                </button>
            </div>

            <div className="mt-6">
                <SubLabel>Description</SubLabel>
                <textarea
                    rows={3}
                    value={wh.description || ""}
                    onChange={(e) => update("work_history.description", e.target.value)}
                    className="pg-input mt-1.5"
                    style={{ resize: "vertical", minHeight: 90 }}
                    data-testid="admin-content-wh-description"
                />
            </div>

            {/* Collab brand tiles */}
            <div className="mt-8">
                <div className="mb-2 flex items-center justify-between">
                    <SubLabel>Collaboration Brand Tiles</SubLabel>
                    <button
                        type="button"
                        onClick={() => {
                            const next = [...collabSorted, { id: newId("cb"), name: "New Brand", order: collabSorted.length }];
                            update("work_history.collab_brands", next);
                        }}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-content-collab-add"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add Brand
                    </button>
                </div>
                <ul className="space-y-2" data-testid="admin-content-collab-list">
                    {collabSorted.map((b, idx) => (
                        <li key={b.id || idx} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={b.name || ""}
                                onChange={(e) => {
                                    const next = collabSorted.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x));
                                    update("work_history.collab_brands", next);
                                }}
                                className="pg-input flex-1"
                                data-testid={`admin-content-collab-name-${idx}`}
                            />
                            <ReorderButton
                                onUp={() => update("work_history.collab_brands", reorder(collabSorted, idx, -1))}
                                onDown={() => update("work_history.collab_brands", reorder(collabSorted, idx, 1))}
                            />
                            <IconBtn
                                onClick={() => update("work_history.collab_brands", collabSorted.filter((_, i) => i !== idx).map((x, i) => ({ ...x, order: i })))}
                                aria-label="Remove brand"
                                testId={`admin-content-collab-remove-${idx}`}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </IconBtn>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Featured rows */}
            <div className="mt-8">
                <div className="mb-2 flex items-center justify-between">
                    <SubLabel>Featured Project Rows</SubLabel>
                    <button
                        type="button"
                        onClick={() => {
                            const next = [...featuredSorted, { id: newId("fh"), brand: "", scope: "", year: "", order: featuredSorted.length }];
                            update("work_history.featured", next);
                        }}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-content-featured-add"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add Row
                    </button>
                </div>
                <ul className="space-y-3" data-testid="admin-content-featured-list">
                    {featuredSorted.map((r, idx) => (
                        <li
                            key={r.id || idx}
                            className="grid grid-cols-1 gap-2 rounded-2xl border p-4 sm:grid-cols-[1.4fr_1.6fr_0.6fr_auto_auto]"
                            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                            data-testid={`admin-content-featured-${idx}`}
                        >
                            <Field label="Brand" value={r.brand || ""} onChange={(v) => {
                                const next = featuredSorted.map((x, i) => (i === idx ? { ...x, brand: v } : x));
                                update("work_history.featured", next);
                            }} testId={`admin-content-featured-brand-${idx}`} />
                            <Field label="Scope" value={r.scope || ""} onChange={(v) => {
                                const next = featuredSorted.map((x, i) => (i === idx ? { ...x, scope: v } : x));
                                update("work_history.featured", next);
                            }} testId={`admin-content-featured-scope-${idx}`} />
                            <Field label="Year" value={r.year || ""} onChange={(v) => {
                                const next = featuredSorted.map((x, i) => (i === idx ? { ...x, year: v } : x));
                                update("work_history.featured", next);
                            }} testId={`admin-content-featured-year-${idx}`} />
                            <div className="flex items-end gap-2">
                                <ReorderButton
                                    onUp={() => update("work_history.featured", reorder(featuredSorted, idx, -1))}
                                    onDown={() => update("work_history.featured", reorder(featuredSorted, idx, 1))}
                                />
                            </div>
                            <div className="flex items-end">
                                <IconBtn
                                    onClick={() => update("work_history.featured", featuredSorted.filter((_, i) => i !== idx).map((x, i) => ({ ...x, order: i })))}
                                    aria-label="Remove row"
                                    testId={`admin-content-featured-remove-${idx}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </IconBtn>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Current roles */}
            <div className="mt-8">
                <div className="mb-2 flex items-center justify-between">
                    <SubLabel>Currently Leading (Ongoing Roles)</SubLabel>
                    <button
                        type="button"
                        onClick={() => {
                            const next = [...currentSorted, { id: newId("cr"), brand: "", role: "", sector: "", order: currentSorted.length }];
                            update("work_history.current_roles", next);
                        }}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-content-current-add"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add Role
                    </button>
                </div>
                <ul className="space-y-3" data-testid="admin-content-current-list">
                    {currentSorted.map((r, idx) => (
                        <li
                            key={r.id || idx}
                            className="grid grid-cols-1 gap-2 rounded-2xl border p-4 sm:grid-cols-[1.2fr_1.6fr_1.2fr_auto_auto]"
                            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                            data-testid={`admin-content-current-${idx}`}
                        >
                            <Field label="Brand" value={r.brand || ""} onChange={(v) => {
                                const next = currentSorted.map((x, i) => (i === idx ? { ...x, brand: v } : x));
                                update("work_history.current_roles", next);
                            }} testId={`admin-content-current-brand-${idx}`} />
                            <Field label="Role" value={r.role || ""} onChange={(v) => {
                                const next = currentSorted.map((x, i) => (i === idx ? { ...x, role: v } : x));
                                update("work_history.current_roles", next);
                            }} testId={`admin-content-current-role-${idx}`} />
                            <Field label="Sector" value={r.sector || ""} onChange={(v) => {
                                const next = currentSorted.map((x, i) => (i === idx ? { ...x, sector: v } : x));
                                update("work_history.current_roles", next);
                            }} testId={`admin-content-current-sector-${idx}`} />
                            <div className="flex items-end gap-2">
                                <ReorderButton
                                    onUp={() => update("work_history.current_roles", reorder(currentSorted, idx, -1))}
                                    onDown={() => update("work_history.current_roles", reorder(currentSorted, idx, 1))}
                                />
                            </div>
                            <div className="flex items-end">
                                <IconBtn
                                    onClick={() => update("work_history.current_roles", currentSorted.filter((_, i) => i !== idx).map((x, i) => ({ ...x, order: i })))}
                                    aria-label="Remove role"
                                    testId={`admin-content-current-remove-${idx}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </IconBtn>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </SectionCard>
    );
};

// ---------------- Reusable UI ----------------

const SectionCard = ({ title, subtitle, children }) => (
    <section
        className="pg-app-surface p-5 sm:p-7"
        data-testid={`admin-content-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
        <div className="mb-5">
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>{title}</h2>
            {subtitle && (
                <p className="mt-1.5 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                    {subtitle}
                </p>
            )}
        </div>
        {children}
    </section>
);

const SubLabel = ({ children }) => (
    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
        {children}
    </span>
);

const Field = ({ label, value, onChange, placeholder, testId }) => (
    <div className="flex flex-col gap-1.5">
        <SubLabel>{label}</SubLabel>
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || ""}
            className="pg-input"
            data-testid={testId}
        />
    </div>
);

const ReorderButton = ({ onUp, onDown }) => (
    <span className="inline-flex flex-col">
        <button
            type="button"
            onClick={onUp}
            aria-label="Move up"
            className="rounded-t-md border px-1.5 py-0.5 transition-colors hover:bg-[var(--pg-surface-2)]"
            style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
        >
            <ChevronUp className="h-3 w-3" />
        </button>
        <button
            type="button"
            onClick={onDown}
            aria-label="Move down"
            className="-mt-px rounded-b-md border px-1.5 py-0.5 transition-colors hover:bg-[var(--pg-surface-2)]"
            style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
        >
            <ChevronDown className="h-3 w-3" />
        </button>
    </span>
);

const IconBtn = ({ onClick, children, "aria-label": ariaLabel, testId }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-[var(--pg-surface-2)]"
        style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
    >
        {children}
    </button>
);
